import {LatLngLiteral} from 'leaflet';
import React from 'react';
import {ControlType} from './SplitProvider';
import {calculatePlayers} from 'utils/trackHelpers';
import {getStoredCompetitorColor} from 'utils/competitorColors';

export type TrackPoint = LatLngLiteral & {
  time: number;
};

export type SiDataType = {
  siCard: number;
  start: string;
  finish: string;
  controls: Array<{code: number; time: string}>;
};

export type CompetitorType = {
  id: number;
  name: string;
  color: string;
  shortName: string;
  deviceName: string;
  track: Array<TrackPoint>;
  trackCompress: Array<number[]>;

  startTime: string;

  offsetTime: number;

  options: {
    show: boolean;
    fullRoute: boolean;
    highlight: boolean;
  };
  display: {
    track: TrackPoint[];
    color: string;
  };
  splits: {
    track: TrackPoint[];
    time: number;
    distance: number;
  }[];
  distance: number;
  time: number;
  diff: number;
  siData: SiDataType;
};

export type MapType = {
  id: number;
  name: string;
  topLeft: LatLngLiteral;
  topRight: LatLngLiteral;
  bottomLeft: LatLngLiteral;
  url: string;
};

export type RouteType = {
  id: number;
  name: string;
  color: string;
  track: TrackPoint[];
};

export type MarkerType = 'circle' | 'arrow';

export type SubEventOptionType = {
  name: string;
  sub: string;
};

export type EventType = {
  slug: string;
  sub: string;
  startDate: string;
  endDate: string;
  isLive: boolean;
  status: string;
  isOpen: boolean;
  name: string;
  location: LatLngLiteral;
  zoom: number;
  delay: number;
  markerType: MarkerType;

  competitors: Array<CompetitorType>;
  maps: Array<MapType>;
  routes: Array<RouteType>;
  controlsAnalytic: Array<ControlType>;
  subEvents: Array<SubEventOptionType>;
};

export type OptionsType = {
  showAllPlayers: boolean;

  timeGradient: boolean;
  opacity: number;
  weight: number;
};

export type EventContextType = {
  event: EventType;
  options: OptionsType;
  updateOptions: React.Dispatch<React.SetStateAction<OptionsType>>;
  players: Array<CompetitorType>;
  updatePlayers: React.Dispatch<React.SetStateAction<CompetitorType[]>>;
  visiblePlayers: Array<CompetitorType>;
  updateVisiblePlayers: React.Dispatch<React.SetStateAction<CompetitorType[]>>;
  showControls: boolean;
  setShowControls: React.Dispatch<React.SetStateAction<boolean>>;
};

export const EventContext = React.createContext<EventContextType>({
  event: {
    slug: '',
    sub: '',
    name: '',
    startDate: '',
    endDate: '',
    isLive: false,
    status: '',
    isOpen: false,
    location: {lat: 0, lng: 0},
    zoom: 1,
    delay: 30,
    markerType: 'circle',

    competitors: [],
    maps: [],
    routes: [],
    controlsAnalytic: [],
    subEvents: []
  },
  options: {
    showAllPlayers: true,
    timeGradient: false,
    opacity: 1,
    weight: 1
  },
  updateOptions: () => {},
  players: [],
  updatePlayers: () => {},
  visiblePlayers: [],
  updateVisiblePlayers: () => {},
  showControls: false,
  setShowControls: () => {}
});

export const EventContextProvider = ({children, value}: EventContextProviderProps) => {
  const [options, setOptions] = React.useState<OptionsType>(value.options);
  const [players, setPlayers] = React.useState<Array<CompetitorType>>(
    calculatePlayers(
      value.event.competitors.filter((competitor) => competitor.track.length > 0),
      value.event.controlsAnalytic
    ).map((competitor) => {
      const storedColor = getStoredCompetitorColor(competitor.deviceName);
      const color = storedColor || competitor.color;
      return {
        ...competitor,
        color,
        display: {...competitor.display, color},
        options: {show: true, fullRoute: false, highlight: false}
      };
    })
  );
  const [visiblePlayers, setVisiblePlayers] = React.useState<Array<CompetitorType>>([]);
  const [showControls, setShowControls] = React.useState(false);

  return (
    <EventContext.Provider
      value={{
        event: value.event,
        options,
        players,
        updateOptions: setOptions,
        updatePlayers: setPlayers,
        visiblePlayers,
        updateVisiblePlayers: setVisiblePlayers,
        showControls,
        setShowControls
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

export interface EventContextProviderProps {
  children: React.ReactNode;
  value: {event: EventType; options: OptionsType};
}

export const useEventContext = () => {
  const context = React.useContext<EventContextType>(EventContext);

  return context;
};
